import { MIN_OPTION_COUNT, NOTE_NAMES, type NoteName } from '@cavaquinho/shared';
import { noteLabel } from '../../../lib/notes.js';
import type { NoteIdentificationConfig } from '../../../lib/api/types.js';
import { OptionRow } from '../../../ui/primitives/OptionRow.tsx';
import { Text } from '../../../ui/primitives/Text.tsx';
import { DrawerSection } from '../../../ui/components/Drawer/Drawer.tsx';
import { AudioUpload } from '../AudioUpload.tsx';
import { VisibleAnswersField, fitVisibleAnswers } from '../VisibleAnswersField.tsx';
import { violationsFor, type FieldViolation } from '../validation.js';
import styles from './ConfigFields.module.css';

interface ConfigFieldsProps {
  value: NoteIdentificationConfig;
  violations: readonly FieldViolation[];
  onChange: (next: NoteIdentificationConfig) => void;
}

/**
 * The `note_identification` configuration form (FR-007, FR-009, FR-010,
 * FR-040).
 *
 * THE ONE FILE A NEW EXERCISE TYPE ADDS ON THE ADMIN SIDE. `ExerciseConfigForm`
 * picks it from a registry by type key and knows nothing about notes;
 * everything specific to this type lives here.
 *
 * Two named sections (§ 14 Drawer): the clip, and the notes it may be
 * answered with. Each note is one `OptionRow` — the same control
 * `multiple_choice`'s answers use — because the question it answers is
 * identical: is this note offered at all, and is it the correct one.
 * Marking a note correct implies including it; the reverse is not automatic,
 * so removing the correct note from the pool falls back to another included
 * one, mirroring `QuestionOptionsFields.removeOption`. Beneath the pool,
 * `visibleAnswers` says how many of those notes one attempt shows.
 */
export function ConfigFields({ value, violations, onChange }: ConfigFieldsProps) {
  const pool = new Set(value.notePool);

  function setPool(next: ReadonlySet<NoteName>, correctNote: NoteName) {
    onChange({
      ...value,
      notePool: NOTE_NAMES.filter((name) => next.has(name)),
      correctNote,
      visibleAnswers: fitVisibleAnswers(value.visibleAnswers, next.size),
    });
  }

  function toggleIncluded(note: NoteName, included: boolean) {
    // The floor is enforced by refusing the toggle rather than disabling the
    // control (FR-041's lower bound on the number of options presented).
    if (!included && pool.size <= MIN_OPTION_COUNT) return;

    const next = new Set(pool);
    if (included) next.add(note);
    else next.delete(note);

    const correctNote = next.has(value.correctNote)
      ? value.correctNote
      : (NOTE_NAMES.find((name) => next.has(name)) ?? value.correctNote);
    setPool(next, correctNote);
  }

  function selectCorrect(note: NoteName) {
    // Choosing a note as the answer implies it is offered as an option.
    setPool(new Set(pool).add(note), note);
  }

  const poolViolations = violationsFor(violations, 'config.notePool');
  const correctViolations = violationsFor(violations, 'config.correctNote');

  return (
    <div data-testid="note-identification-config">
      <DrawerSection
        title="Áudio"
        description="O trecho que o aluno ouve antes de responder."
        testId="audio-section"
      >
        <AudioUpload
          audioUrl={value.audioUrl}
          violations={violations}
          onChange={(audioUrl) => onChange({ ...value, audioUrl })}
        />
      </DrawerSection>

      <DrawerSection
        title="Notas"
        description="Marque à esquerda qual nota é a correta, e quais aparecem como opções."
        testId="notes-section"
      >
        <fieldset className={styles.pool} data-testid="note-pool">
          <legend className={styles.legend}>
            <Text as="span" variant="body-sm" tone="muted">
              {pool.size === NOTE_NAMES.length
                ? 'Todas as sete notas'
                : pool.size === 1
                  ? '1 nota selecionada'
                  : `${pool.size} notas selecionadas`}
            </Text>
          </legend>

          <div className={styles.rows}>
            {NOTE_NAMES.map((note) => (
              <OptionRow
                key={note}
                name="correctNote"
                label={noteLabel(note, 'pt-BR')}
                correct={value.correctNote === note}
                radioLabel={`Marcar "${noteLabel(note, 'pt-BR')}" como a resposta correta`}
                onCorrectSelect={() => selectCorrect(note)}
                included={pool.has(note)}
                onIncludedChange={(included) => toggleIncluded(note, included)}
              />
            ))}
          </div>

          {poolViolations.map((violation) => (
            <Text key={violation.code} as="p" variant="body-sm" tone="error">
              {violation.message}
            </Text>
          ))}
          {correctViolations.map((violation) => (
            <Text key={violation.code} as="p" variant="body-sm" tone="error">
              {violation.message}
            </Text>
          ))}
        </fieldset>

        <VisibleAnswersField
          value={value.visibleAnswers}
          answerCount={pool.size}
          violations={violations}
          onChange={(visibleAnswers) => onChange({ ...value, visibleAnswers })}
        />
      </DrawerSection>
    </div>
  );
}

/** What a new `note_identification` Exercise starts from in the form. */
export function emptyNoteIdentificationConfig(): NoteIdentificationConfig {
  return {
    audioUrl: '',
    notePool: [...NOTE_NAMES],
    correctNote: NOTE_NAMES[0],
  };
}
